import type { AnyRouter } from '@tanstack/react-router'

/**
 * Version-drift reload.
 *
 * When a new build deploys mid-session, the running client keeps executing the OLD bundle. Now
 * that hashed assets persist on the CDN (see apps/server/scripts/upload-to-cdn.ts), the old client
 * never hits a missing-chunk error — so it would otherwise run stale code indefinitely. To pick up
 * the new version without yanking the page out from under the user, we reload on their NEXT
 * navigation:
 *
 *  - The server stamps every dynamic response with the deployed commit hash (APP_VERSION_HEADER,
 *    see apps/server/server.bun.ts) and bakes it into the SSR document as `window.COMMIT_HASH`.
 *  - The client captures the latest advertised version from response headers (every tRPC/loader
 *    fetch carries it) and, on the next route resolution, full-reloads if it differs from the
 *    version this document was served with.
 *
 * A short cooldown guards against a reload loop while a rollout briefly serves two versions behind
 * the load balancer.
 */

// Wire constant — kept in sync with apps/server/server.bun.ts (no shared package between the two).
export const APP_VERSION_HEADER = 'x-danchiano-app-version'

const RELOAD_GUARD_KEY = 'danchiano:version-reload-at'
const PRELOAD_RELOAD_GUARD_KEY = 'danchiano:preload-reload-at'
const RELOAD_COOLDOWN_MS = 30_000

let latestSeenVersion = ''

function reloadedRecentlyFor(key: string): boolean {
  try {
    const at = window.sessionStorage.getItem(key)
    return !!at && Date.now() - Number(at) < RELOAD_COOLDOWN_MS
  } catch {
    return false
  }
}

/**
 * Reload once to recover from a failed chunk import — a `vite:preloadError`, fired when a dynamic
 * import can't be fetched (a chunk deleted by a newer deploy, or a transient CDN/CORS hiccup).
 *
 * Guarded so a *persistent* failure can't spin the page in an infinite reload loop: if we already
 * reloaded within the cooldown, we bail and let the rejected import surface through the route error
 * boundary instead. Returns true if a reload was triggered.
 */
export function reloadForPreloadError(): boolean {
  if (reloadedRecentlyFor(PRELOAD_RELOAD_GUARD_KEY)) return false
  try {
    window.sessionStorage.setItem(PRELOAD_RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    // sessionStorage unavailable (private mode / sandboxed iframe): reload anyway. Without the guard
    // a truly persistent failure could loop, but the alternative — never recovering from a stale
    // chunk — is worse, and this path is rare.
  }
  window.location.reload()
  return true
}

/** The deployed version this running document/bundle was served as (baked SSR-side; see __root). */
function loadedVersion(): string {
  return (typeof window !== 'undefined' && window.COMMIT_HASH) || ''
}

/** Wrap `window.fetch` once to record the version the server advertises on every response. */
function captureVersionFromFetch() {
  const original = window.fetch
  // Object.assign preserves the static members of `fetch` (e.g. `fetch.preconnect`) on the wrapper.
  window.fetch = Object.assign((...args: Parameters<typeof fetch>) => {
    return original(...args).then((response) => {
      const version = response.headers.get(APP_VERSION_HEADER)
      if (version) latestSeenVersion = version
      return response
    })
  }, original)
}

function reloadedRecently(): boolean {
  return reloadedRecentlyFor(RELOAD_GUARD_KEY)
}

/**
 * Arm the drift check on a client router. No-op when no version is stamped (dev / e2e build),
 * so local development never triggers reloads.
 */
export function setupVersionDriftReload(router: AnyRouter) {
  if (loadedVersion() === '') return
  captureVersionFromFetch()
  router.subscribe('onResolved', () => {
    if (!latestSeenVersion || latestSeenVersion === loadedVersion()) return
    if (reloadedRecently()) return
    try {
      window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
    } catch {
      // sessionStorage unavailable (private mode / sandboxed iframe) — reload anyway; the
      // worst case is one extra reload, which still lands the user on the new version.
    }
    window.location.reload()
  })
}
